import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { useDispatch, useSelector } from "react-redux";
import { updatePmFrame } from "../actions/ProductionMonitorAction";

const NAVY = "#142c54";

/* -------------------- Helpers -------------------- */
const toYmd = (v) => String(v || "").slice(0, 10);

// Typed digits -> yyyy-MM-dd as you type
const formatDateTyping = (raw) => {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
};

const isValidDateText = (t) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t || "")) return false;
  const [y, m, d] = t.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

// Frame / OCL No rules (same code is in InsertFrames.js and Editframedialog.js)
//   Frame -24 Port Side | Frame -24 Starboard Side | Frame -24 Center | Frame 12 | Frame -20-300 Center
//   OCL 700 Port Side   | OCL 700 Starboard Side   | OCL 700 Center   | OCL 05

const FRAME_SIDES = [
  { key: "p", label: "Port Side" },
  { key: "s", label: "Starboard Side" },
  { key: "c", label: "Center" },
];

const FRAME_NO_HINT = "Use Frame -24 / OCL 700 + Port Side / Starboard Side / Center";

const SIDE_RE = "(Port Side|Starboard Side|Center)";
const PREFIXES = ["frame", "ocl"];

// frame / FRAME -> Frame, ocl / Ocl -> OCL
const fixFrameNo = (text) =>
  String(text || "")
    .replace(/\bframe(?![a-z])/gi, "Frame")
    .replace(/\bocl(?![a-z])/gi, "OCL");

// While typing: can this text still become a valid value?
const canTypeFrameNo = (text) => {
  const t = String(text || "");
  const lower = t.toLowerCase();
  if (!t) return true;
  if (/^\s/.test(t)) return false; // no leading space
  if (PREFIXES.some((p) => p.startsWith(lower))) return true; // "F", "Fra", "OC"

  const prefix = PREFIXES.find((p) => lower.startsWith(p));
  if (!prefix) return false;

  const rest = lower.slice(prefix.length);
  if (/^ ?-? ?(\d+(-\d+)*-?)?$/.test(rest)) return true; // "Frame -24", "Frame -20-300", "OCL 700"
  const m = /^ ?-? ?\d+(?:-\d+)* (.*)$/.exec(rest); // "OCL 700 Port Si", "Frame -20-300 Cen"
  if (!m) return false;
  return FRAME_SIDES.some((s) => s.label.toLowerCase().startsWith(m[1]));
};

// "frame-24 port side" -> "Frame -24 Port Side", "ocl700 c" -> "OCL 700 Center", "ocl   5" -> "OCL 5"
const normalizeFrameNo = (text) => {
  const t = fixFrameNo(text).trim().replace(/\s+/g, " ");
  const f = new RegExp(`^(Frame|OCL) ?(-)? ?(\\d+(?:-\\d+)*)(?: ${SIDE_RE})?$`, "i").exec(t);
  if (!f) return t;
  const side = f[4] ? FRAME_SIDES.find((s) => s.label.toLowerCase() === f[4].toLowerCase()).label : "";
  return `${f[1]} ${f[2] || ""}${f[3]}${side ? ` ${side}` : ""}`;
};

const isValidFrameNo = (text) => new RegExp(`^(Frame|OCL) -?\\d+(?:-\\d+)*( ${SIDE_RE})?$`).test(normalizeFrameNo(text));

// "Frame -24" / "OCL 700" typed, side not chosen yet -> show Port / Starboard / Center suggestions
const needsFrameSide = (text) => /^(Frame|OCL) ?-? ?\d+(?:-\d+)* ?$/.test(fixFrameNo(text));

// "OCL 700" + "Port Side" -> "OCL 700 Port Side"
const withFrameSide = (text, side) => `${normalizeFrameNo(text)} ${side}`;

// Handle one change in the box. Returns the new value, or null to ignore the key.
//  - p / s / c after the number -> Port Side / Starboard Side / Center (Frame and OCL)
//  - backspace inside a side -> removes the whole side
//  - only Frame.. / OCL.. can be typed
const applyFrameNoTyping = (prev, raw) => {
  const before = String(prev || "");
  const v = fixFrameNo(raw);

  if (v.length < before.length) {
    const full = new RegExp(`^((?:Frame|OCL) ?-? ?\\d+(?:-\\d+)*) ${SIDE_RE}$`).exec(before);
    if (full && v.startsWith(full[1]) && v.length > full[1].length) return `${full[1]} `;
    return v; // deleting is always allowed
  }

  const short = /^((?:Frame|OCL) ?-? ?\d+(?:-\d+)*) ?([psc])$/i.exec(v);
  if (short) {
    const side = FRAME_SIDES.find((s) => s.key === short[2].toLowerCase()).label;
    return withFrameSide(short[1], side);
  }

  return canTypeFrameNo(v) ? v : null;
};

// Build the form from a frame from GetFrameDetails
const formFromFrame = (f) => ({
  iwoNo: String(f?.IwoNo ?? ""),
  componentName: f?.ComponentName || "",
  frameNo: f?.FrameNo || "",
  startDate: toYmd(f?.StartDate),
  endDate: toYmd(f?.EndDate),
  weight: f?.Weight === null || f?.Weight === undefined ? "" : String(f.Weight),
  parts: (f?.Parts || []).map((p, i) => ({
    key: `old-${p.PartId ?? p.partId ?? i}`,
    partId: String(p.PartId ?? p.partId ?? ""),
    partNo: String(p.PartNo ?? p.partNo ?? ""),
    original: String(p.PartNo ?? p.partNo ?? ""),
  })),
});

let newPartSeq = 0;

/* -------------------- Edit Frame dialog -------------------- */
const EditFrameDialog = ({ open, frame, iwoList, components, frameList = [], onClose, onSaved }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { frameUpdateLoading } = useSelector((state) => state.productionMonitor);

  const [form, setForm] = useState(() => formFromFrame(frame));
  const [error, setError] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  // reset every time a frame is opened
  useEffect(() => {
    if (open) {
      setForm(formFromFrame(frame));
      setError("");
      setShowErrors(false);
    }
  }, [open, frame]);

  const original = useMemo(() => formFromFrame(frame), [frame]);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const iwoOptions = useMemo(() => {
    const list = [...(iwoList || [])];
    // keep the current IWO selectable even if it is not in the list
    if (form.iwoNo && !list.some((x) => String(x.IwoNo) === form.iwoNo)) list.unshift({ IwoNo: form.iwoNo, EngSpec: "" });
    return list;
  }, [iwoList, form.iwoNo]);

  // another frame of the chosen IWO is already EDGE Grinding (only one per IWO)
  const edgeTaken = (frameList || []).some(
    (f) => f.FrameId !== frame?.FrameId && String(f.IwoNo) === String(form.iwoNo) && f.ComponentName === "EDGE_GRINDING"
  );

  const componentOptions = useMemo(() => {
    const list = [...(components || [])];
    if (form.componentName && !list.some((c) => c.value === form.componentName)) {
      list.push({ value: form.componentName, label: form.componentName.replace(/_/g, " ") });
    }
    return list;
  }, [components, form.componentName]);

  /* ---- parts ---- */
  const updatePart = (key, value) =>
    setForm((prev) => ({ ...prev, parts: prev.parts.map((p) => (p.key === key ? { ...p, partNo: value } : p)) }));
  const addPart = () =>
    setForm((prev) => ({ ...prev, parts: [...prev.parts, { key: `new-${++newPartSeq}`, partId: "", partNo: "", original: "" }] }));
  const removeNewPart = (key) => setForm((prev) => ({ ...prev, parts: prev.parts.filter((p) => p.key !== key) }));

  /* ---- what changed (only these go to the API) ---- */
  const changes = useMemo(() => {
    const c = {};
    ["iwoNo", "componentName", "frameNo", "startDate", "endDate"].forEach((k) => {
      const value = k === "frameNo" ? normalizeFrameNo(form[k]) : String(form[k]).trim();
      if (value !== String(original[k]).trim()) c[k] = value;
    });
    const changedParts = form.parts
      .filter((p) => (p.partId ? p.partNo.trim() !== p.original : p.partNo.trim() !== ""))
      .map((p) => ({ partId: p.partId, partNo: p.partNo.trim() }));
    if (changedParts.length) c.parts = changedParts;
    // weight goes as a number, only when changed ("1180" and "1180.0" are the same)
    const w = String(form.weight).trim();
    const oldW = String(original.weight).trim();
    if (w !== "" && (oldW === "" || Number(w) !== Number(oldW))) c.weight = Number(w);
    return c;
  }, [form, original]);

  const hasChanges = Object.keys(changes).length > 0;

  /* ---- validation ---- */
  const errors = useMemo(() => {
    const e = {};
    if (!form.iwoNo) e.iwoNo = "Required";
    if (!form.componentName) e.componentName = "Required";
    else if (form.componentName === "EDGE_GRINDING" && edgeTaken) e.componentName = "EDGE Grinding already added for this IWO";
    // Frame / OCL No is optional for EDGE Grinding (still checked when entered)
    if (!form.frameNo.trim()) {
      if (form.componentName !== "EDGE_GRINDING") e.frameNo = "Required";
    } else if (!isValidFrameNo(form.frameNo)) e.frameNo = FRAME_NO_HINT;
    if (!isValidDateText(form.startDate)) e.startDate = form.startDate ? "Invalid date" : "Required";
    if (!isValidDateText(form.endDate)) e.endDate = form.endDate ? "Invalid date" : "Required";
    if (!e.startDate && !e.endDate && form.startDate > form.endDate) e.endDate = "Before start date";
    if (String(form.weight).trim() === "") {
      if (String(original.weight).trim() !== "") e.weight = "Required"; // can't clear a saved weight
    } else if (!/^\d+(\.\d+)?$/.test(String(form.weight)) || Number(form.weight) <= 0) {
      e.weight = "Invalid weight";
    }

    const seen = {};
    form.parts.forEach((p) => {
      const v = p.partNo.trim();
      if (p.partId && !v) e[p.key] = "Required";
      if (v) {
        if (seen[v]) e[p.key] = "Duplicate";
        seen[v] = true;
      }
    });
    return e;
  }, [form, original, edgeTaken]);

  const err = (k) => (showErrors ? errors[k] : "");

  const handleSave = async () => {
    setShowErrors(true);
    setError("");
    if (Object.keys(errors).length || !hasChanges) return;

    const result = await dispatch(
      updatePmFrame({
        frameId: String(frame.FrameId),
        ...changes,
        updatedBy: localStorage.getItem("employeeId") || "",
      })
    );

    if (result.success) {
      onSaved && onSaved(`${changes.frameNo || frame.FrameNo} updated`);
    } else {
      setError(result.message);
    }
  };

  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };
  const changedSx = (k) => (changes[k] !== undefined ? { "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff8e6" } } : fieldSx);

  if (!frame) return null;

  return (
    <Dialog
      open={open}
      onClose={frameUpdateLoading ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : "16px" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: NAVY, fontWeight: 700, pr: 1 }}>
        Edit {frame.FrameNo}
        <Chip size="small" label={`ID ${frame.FrameId}`} sx={{ fontSize: "11px", height: 20 }} />
        <IconButton onClick={onClose} disabled={frameUpdateLoading} sx={{ ml: "auto" }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <Autocomplete
            options={iwoOptions}
            value={iwoOptions.find((x) => String(x.IwoNo) === form.iwoNo) || null}
            onChange={(_, v) => set("iwoNo", v ? String(v.IwoNo) : "")}
            getOptionLabel={(o) => (o.EngSpec ? `${o.IwoNo} - ${o.EngSpec}` : String(o.IwoNo))}
            isOptionEqualToValue={(a, b) => String(a.IwoNo) === String(b.IwoNo)}
            sx={{ gridColumn: { sm: "1 / -1" } }}
            renderInput={(params) => (
              <TextField {...params} label="IWO No" error={!!err("iwoNo")} helperText={err("iwoNo")} sx={changedSx("iwoNo")} />
            )}
          />

          <TextField
            select
            label="Component"
            value={form.componentName}
            onChange={(e) => set("componentName", e.target.value)}
            error={!!err("componentName")}
            helperText={err("componentName")}
            sx={changedSx("componentName")}
          >
            {componentOptions.map((c) => {
              // EDGE Grinding: only one per IWO (shown, but can't be picked if another frame of this IWO has it)
              const blocked = c.value === "EDGE_GRINDING" && form.componentName !== "EDGE_GRINDING" && edgeTaken;
              return (
                <MenuItem key={c.value} value={c.value} disabled={blocked}>
                  {blocked ? `${c.label} (added)` : c.label}
                </MenuItem>
              );
            })}
          </TextField>

          <Box>
            <TextField
              fullWidth
              label="Frame / OCL No"
              placeholder={form.componentName === "EDGE_GRINDING" ? "Optional" : "Frame -24 / OCL 700"}
              value={form.frameNo}
              onChange={(e) => {
                const next = applyFrameNoTyping(form.frameNo, e.target.value);
                if (next === null) return; // only Frame.. / OCL.. can be typed
                set("frameNo", next);
              }}
              error={!!err("frameNo")}
              helperText={err("frameNo")}
              sx={changedSx("frameNo")}
            />
            {/* "Frame -24" typed -> pick the side (or type P / S / C) */}
            {needsFrameSide(form.frameNo) && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                {FRAME_SIDES.map((s) => (
                  <Chip
                    key={s.key}
                    size="small"
                    clickable
                    label={`${s.key.toUpperCase()} · ${s.label}`}
                    onClick={() => set("frameNo", withFrameSide(form.frameNo, s.label))}
                    sx={{ fontSize: "11px", backgroundColor: "#e8f0fa", color: NAVY, fontWeight: 600 }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <TextField
            label="Start Date"
            placeholder="yyyy-mm-dd"
            value={form.startDate}
            onChange={(e) => set("startDate", formatDateTyping(e.target.value))}
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            InputLabelProps={{ shrink: true }}
            error={!!err("startDate")}
            helperText={err("startDate")}
            sx={changedSx("startDate")}
          />

          <TextField
            label="End Date"
            placeholder="yyyy-mm-dd"
            value={form.endDate}
            onChange={(e) => set("endDate", formatDateTyping(e.target.value))}
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            InputLabelProps={{ shrink: true }}
            error={!!err("endDate")}
            helperText={err("endDate")}
            sx={changedSx("endDate")}
          />

          <TextField
            label="Weight"
            placeholder="0.0"
            value={form.weight}
            onChange={(e) => {
              // numbers only, up to 3 decimals
              const t = e.target.value.replace(/[^\d.]/g, "");
              const [whole, ...rest] = t.split(".");
              set("weight", rest.length ? `${whole}.${rest.join("").slice(0, 3)}` : whole);
            }}
            inputProps={{ inputMode: "decimal", "aria-label": "Weight" }}
            InputProps={{ endAdornment: <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>kg</Typography> }}
            InputLabelProps={{ shrink: true }}
            error={!!err("weight")}
            helperText={err("weight") || (String(form.weight).trim() === "" ? "Not entered yet" : "")}
            sx={changedSx("weight")}
          />
        </Box>

        {/* Parts */}
        <Box sx={{ mt: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>Part Nos</Typography>
            <Typography sx={{ fontSize: "12px", color: "#6b7280", ml: 1 }}>({form.parts.length})</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addPart} sx={{ ml: "auto", textTransform: "none", fontWeight: 600 }}>
              Add part
            </Button>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" }, gap: 1.25 }}>
            {form.parts.map((p, i) => {
              const isNew = !p.partId;
              const changed = !isNew && p.partNo.trim() !== p.original;
              return (
                <TextField
                  key={p.key}
                  size="small"
                  label={isNew ? "New part" : `Part ${i + 1}`}
                  value={p.partNo}
                  onChange={(e) => updatePart(p.key, e.target.value)}
                  error={!!err(p.key)}
                  helperText={err(p.key) || (changed ? `was ${p.original}` : " ")}
                  inputProps={{ "aria-label": isNew ? "New part no" : `Part no ${p.original}` }}
                  InputProps={{
                    endAdornment: isNew ? (
                      <IconButton size="small" edge="end" onClick={() => removeNewPart(p.key)} aria-label="Remove new part">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : changed ? (
                      <Tooltip title="Undo">
                        <IconButton size="small" edge="end" onClick={() => updatePart(p.key, p.original)} aria-label="Undo part change">
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: isNew ? "#eef7ee" : changed ? "#fff8e6" : "#fff",
                    },
                    "& .MuiFormHelperText-root": { mx: 0.5, fontSize: "11px" },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Typography sx={{ fontSize: "12px", color: "#6b7280", mr: "auto" }}>
          {hasChanges ? `${Object.keys(changes).length} change(s)` : "No changes"}
        </Typography>
        <Button onClick={onClose} disabled={frameUpdateLoading} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || frameUpdateLoading}
          startIcon={frameUpdateLoading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", backgroundColor: NAVY }}
        >
          Update Frame
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditFrameDialog;